import { db } from "@/lib/firebase";
import { storage } from "@/lib/firebase";
import { uploadReportMedia } from "@/lib/mediaUpload";
import { logSpotObservationFromReport } from "@/lib/spot-observations";
import { SurfReport, WaveSize, Condition, CrowdLevel } from "@/types/surfReport";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";
import { useCallback, useEffect, useRef } from "react";
import { User } from "firebase/auth";

const REPORTS_COLLECTION = "reports";
const EXPIRE_HOURS = 6;

function expiryThreshold(): Date {
  return new Date(Date.now() - EXPIRE_HOURS * 60 * 60 * 1000);
}

/* ---------- Real-time feed hook ---------- */

function buildReportsQuery(regionId: string) {
  return query(
    collection(db, REPORTS_COLLECTION),
    where("regionId", "==", regionId),
    where("createdAt", ">", Timestamp.fromDate(expiryThreshold())),
    orderBy("createdAt", "desc"),
    limit(50),
  );
}

async function fetchReports(regionId: string): Promise<SurfReport[]> {
  const snap = await getDocs(buildReportsQuery(regionId));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })) as SurfReport[];
}

export function useRegionReports(regionId: string) {
  const queryClient = useQueryClient();
  const listenerFailed = useRef(false);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["reports", regionId] });
  }, [queryClient, regionId]);

  // Real-time listener for live updates
  useEffect(() => {
    listenerFailed.current = false;
    const unsub = onSnapshot(
      buildReportsQuery(regionId),
      (snapshot) => {
        const reports: SurfReport[] = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as SurfReport[];
        queryClient.setQueryData(["reports", regionId], reports);
      },
      () => {
        // Listener dropped — mark stale so React Query refetches via polling
        listenerFailed.current = true;
        invalidate();
      },
    );

    return unsub;
  }, [regionId, queryClient, invalidate]);

  return useQuery<SurfReport[]>({
    queryKey: ["reports", regionId],
    queryFn: () => fetchReports(regionId),
    // Poll more aggressively if the real-time listener has failed
    staleTime: listenerFailed.current ? 10_000 : 30_000,
    refetchInterval: listenerFailed.current ? 15_000 : undefined,
  });
}

/* ---------- Submit report ---------- */

interface SubmitReportParams {
  regionId: string;
  cameraId: string | null;
  cameraName: string | null;
  user: User;
  waveSize: WaveSize;
  conditions: Condition;
  crowd: CrowdLevel;
  comment?: string;
  mediaFile?: File;
}

export async function submitReport({
  regionId,
  cameraId,
  cameraName,
  user,
  waveSize,
  conditions,
  crowd,
  comment,
  mediaFile,
}: SubmitReportParams): Promise<string> {
  // Generate a temp ID for media upload path
  const tempId = crypto.randomUUID();

  // Upload media first so we can include the URL in the doc
  let mediaURL: string | null = null;
  let mediaType: "photo" | "video" | null = null;

  if (mediaFile) {
    const result = await uploadReportMedia(user.uid, tempId, mediaFile);
    mediaURL = result.url;
    mediaType = result.mediaType;
  }

  const reportData: Record<string, unknown> = {
    regionId,
    cameraId: cameraId || null,
    cameraName: cameraName || null,
    userId: user.uid,
    userDisplayName: user.displayName || "Anonymous",
    userPhotoURL: user.photoURL || null,
    waveSize,
    conditions,
    crowd,
    comment: comment?.trim() || null,
    mediaURL,
    mediaType,
    confirmCount: 0,
    denyCount: 0,
    likeCount: 0,
    createdAt: serverTimestamp(),
  };

  try {
    const docRef = await addDoc(collection(db, REPORTS_COLLECTION), reportData);
    void logSpotObservationFromReport({
      regionId,
      breakId: cameraId,
      user,
      waveSize,
      conditions,
    }).catch(() => {
      // Best-effort telemetry; the user report is the source of truth.
    });
    return docRef.id;
  } catch (err) {
    // Clean up orphaned media if the Firestore doc write failed
    if (mediaURL) {
      const ext = mediaType === "video" ? "mp4" : "jpg";
      const mediaRef = ref(storage, `reports/${user.uid}/${tempId}/media.${ext}`);
      try {
        await deleteObject(mediaRef);
      } catch {
        // Best-effort cleanup — don't mask the original error
      }
    }
    throw err;
  }
}

/* ---------- Update report ---------- */

interface UpdateReportParams {
  reportId: string;
  waveSize: WaveSize;
  conditions: Condition;
  crowd: CrowdLevel;
  comment?: string;
}

export async function updateReport({
  reportId,
  waveSize,
  conditions,
  crowd,
  comment,
}: UpdateReportParams): Promise<void> {
  const { updateDoc } = await import("firebase/firestore");
  await updateDoc(doc(db, REPORTS_COLLECTION, reportId), {
    waveSize,
    conditions,
    crowd,
    comment: comment?.trim() || null,
  });
}

/* ---------- Delete report ---------- */

export async function deleteReport(reportId: string): Promise<void> {
  // Read the doc first to get media info for cleanup
  const { getDoc } = await import("firebase/firestore");
  const snap = await getDoc(doc(db, REPORTS_COLLECTION, reportId));
  const data = snap.data();

  await deleteDoc(doc(db, REPORTS_COLLECTION, reportId));

  // Clean up media from Storage if present
  if (data?.mediaURL && data?.userId) {
    try {
      // Parse the storage path from the download URL
      const urlPath = decodeURIComponent(
        new URL(data.mediaURL).pathname.split("/o/")[1]?.split("?")[0] ?? "",
      );
      if (urlPath) {
        const mediaRef = ref(storage, urlPath);
        await deleteObject(mediaRef);
      }
    } catch {
      // Best-effort cleanup — don't fail the delete if media cleanup fails
    }
  }
}
